import React, { useEffect, useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import api from '../api/client';
import { actionBtnSx, tableTagSx } from '../styles/uiTokens';

const categories = [
  '회원/로그인',
  '서비스 이용',
  '자동세차',
  '정기구독/해지',
  '결제/취소/환불',
];

const FaqsPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formQuestion, setFormQuestion] = useState('');
  const [formAnswer, setFormAnswer] = useState('');
  const [formCategory, setFormCategory] = useState('');

  useEffect(() => {
    fetchFaqs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, categoryFilter]);

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/faqs', {
        params: { page, limit, category: categoryFilter || undefined },
      });
      setFaqs(res.data.faqs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('FAQ 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingFaq(null);
    setFormQuestion('');
    setFormAnswer('');
    setFormCategory('');
    setModalOpen(true);
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormQuestion(faq.question);
    setFormAnswer((faq.answer || '').replace(/<br\s*\/?>/g, '\n'));
    setFormCategory(faq.category || '');
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formQuestion.trim()) {
      alert('질문을 입력해주세요.');
      return;
    }
    const answerWithBr = formAnswer.replace(/\n/g, '<br>');

    try {
      if (editingFaq) {
        await api.put(`/admin/faqs/${editingFaq._id}`, {
          question: formQuestion,
          answer: answerWithBr,
          category: formCategory,
        });
        alert('수정 완료');
      } else {
        await api.post('/admin/faqs', {
          question: formQuestion,
          answer: answerWithBr,
          category: formCategory,
        });
        alert('등록 완료');
      }
      setModalOpen(false);
      fetchFaqs();
    } catch (err) {
      alert(editingFaq ? '수정 실패' : '등록 실패');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말로 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/admin/faqs/${id}`);
      fetchFaqs();
    } catch (err) {
      alert('삭제 실패');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.slice(0, 10);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        FAQ 전체 목록
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>카테고리</InputLabel>
          <Select
            value={categoryFilter}
            label="카테고리"
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
          >
            <MenuItem value="">전체</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100, display: { xs: 'none', md: 'inline-flex' } }}>
          <InputLabel>표시</InputLabel>
          <Select
            value={limit}
            label="표시"
            onChange={(e) => { setLimit(e.target.value); setPage(1); }}
          >
            <MenuItem value={10}>10개</MenuItem>
            <MenuItem value={20}>20개</MenuItem>
            <MenuItem value={50}>50개</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        <Table sx={{ '& td[align="center"], & th[align="center"]': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 700 }}>질문</TableCell>
              <TableCell width="15%" align="center" sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>카테고리</TableCell>
              <TableCell width="15%" align="center" sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>작성일</TableCell>
              <TableCell width="20%" align="center" sx={{ fontWeight: 700 }}>관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  로딩 중...
                </TableCell>
              </TableRow>
            ) : faqs.length > 0 ? (
              faqs.map((faq) => (
                <TableRow key={faq._id} hover>
                  <TableCell sx={{ fontWeight: 500, wordBreak: 'keep-all', minWidth: 200 }}>
                    {faq.question}
                  </TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Chip label={faq.category || '-'} size="small" variant="outlined" sx={tableTagSx(112)} />
                  </TableCell>
                  <TableCell align="center" sx={{ color: '#555', display: { xs: 'none', md: 'table-cell' } }}>
                    {formatDate(faq.createdAt)}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
                      <Button variant="contained" onClick={() => handleEdit(faq)} sx={actionBtnSx}>
                        수정
                      </Button>
                      <Button variant="contained" color="error" onClick={() => handleDelete(faq._id)} sx={actionBtnSx}>
                        삭제
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#888' }}>
                  등록된 FAQ가 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, gap: 2, flexWrap: 'wrap' }}>
        {totalPages > 1 ? (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size="small"
            siblingCount={0}
            boundaryCount={1}
          />
        ) : <Box />}
        <Button variant="contained" onClick={handleCreate} sx={actionBtnSx}>
          추가
        </Button>
      </Box>

      {/* 생성/수정 모달 */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingFaq ? 'FAQ 수정' : 'FAQ 등록'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>카테고리</InputLabel>
            <Select
              value={formCategory}
              label="카테고리"
              onChange={(e) => setFormCategory(e.target.value)}
            >
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="질문"
            fullWidth
            value={formQuestion}
            onChange={(e) => setFormQuestion(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="답변"
            fullWidth
            multiline
            rows={6}
            value={formAnswer}
            onChange={(e) => setFormAnswer(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingFaq ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FaqsPage;
